import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import '../search/SearchPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import MangaImageLightbox from '../../components/manga/MangaImageLightbox';
import MangaApiService from '../../services/MangaApiService';
import ApiService from '../../services/ApiService';
import MangaAccessNotice from './MangaAccessNotice';
import MangaStudioNav from './MangaStudioNav';
import { fetchAllTeamMangas,
    getImageUploadErrorMessage,
    getMangaId,
    getMangaTitle,
    getRejectionReason,
    getTeamId,
    isDeletedStatus,
    isRejectedStatus,
    normalizeStatus,
    translateStatus,
    unwrapList,
    unwrapResponse,
    getMangaErrorMessage,
} from './mangaStudioUtils';
import placeholderCover from '../../assets/card_placeholder.png';

const emptyMangaForm = {
    title: '',
    description: '',
    genres: '',
    author: '',
    publisher: '',
    animeId: '',
    isNsfw: false,
    isCompleted: false,
    coverFileId: '',
    coverStorageBucket: '',
};

const isRoleActive = (value) => value === true || value === 'true' || value === 1 || value === '1';
const COVER_MAX_SIZE_BYTES = 3 * 1024 * 1024;

const splitGenreList = (value) => String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

// Quick picks from the canonical genre list, so teams don't invent new spellings.
const GenrePicker = ({ value, options, disabled, onChange }) => {
    if (!options.length) {
        return null;
    }
    const selected = splitGenreList(value);
    const selectedLower = selected.map((genre) => genre.toLowerCase());
    const toggle = (genre) => {
        const isSelected = selectedLower.includes(genre.toLowerCase());
        const next = isSelected
            ? selected.filter((item) => item.toLowerCase() !== genre.toLowerCase())
            : [...selected, genre];
        onChange(next.join(', '));
    };
    return (
        <div className="ui-chips manga-genre-picker" role="group" aria-label="Жанры">
            {options.map((genre) => (
                <button
                    key={genre}
                    type="button"
                    className="ui-chip"
                    aria-pressed={selectedLower.includes(genre.toLowerCase())}
                    onClick={() => toggle(genre)}
                    disabled={disabled}
                >
                    {genre}
                </button>
            ))}
        </div>
    );
};

const MangaStudioMangaPage = () => {
    const [team, setTeam] = useState(null);
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState(null);

    const [mangaList, setMangaList] = useState([]);
    const [mangaLoading, setMangaLoading] = useState(false);
    const [mangaListError, setMangaListError] = useState(null);
    const [mangaFormError, setMangaFormError] = useState(null);
    const [mangaMessage, setMangaMessage] = useState(null);
    const [selectedMangaId, setSelectedMangaId] = useState('');
    const [composeNew, setComposeNew] = useState(false);

    const [newMangaForm, setNewMangaForm] = useState(emptyMangaForm);
    const [editMangaForm, setEditMangaForm] = useState(emptyMangaForm);
    const [genreOptions, setGenreOptions] = useState([]);
    const [titleQuery, setTitleQuery] = useState('');

    useEffect(() => {
        MangaApiService.getMangaGenres()
            .then((list) => setGenreOptions(list.filter((genre) => genre?.canonical).map((genre) => genre.name)
                .sort((a, b) => a.localeCompare(b, 'ru'))))
            .catch(() => setGenreOptions([]));
    }, []);
    const [newCoverUploading, setNewCoverUploading] = useState(false);
    const [editCoverUploading, setEditCoverUploading] = useState(false);
    const [coverMessage, setCoverMessage] = useState(null);
    const [editCoverMessage, setEditCoverMessage] = useState(null);
    const [rightsAccepted, setRightsAccepted] = useState(false);
    const [mangaCoverUrls, setMangaCoverUrls] = useState({});
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxTitle, setLightboxTitle] = useState('');
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState(null);
    const [selfProfile, setSelfProfile] = useState(null);
    const [transferTeamId, setTransferTeamId] = useState('');
    const [transferBusy, setTransferBusy] = useState(false);

    const isAllowed = MangaApiService.isAllowedDomain();
    const hasSession = !!localStorage.getItem('sessionKey');
    const teamId = getTeamId(team);
    const selfId = selfProfile?.data?.id || selfProfile?.id || localStorage.getItem('profileId');
    const isModerator = isRoleActive(selfProfile?.data?.mod ?? selfProfile?.mod ?? localStorage.getItem('mod'));
    const isEditor = isRoleActive(selfProfile?.data?.editor ?? selfProfile?.editor ?? localStorage.getItem('editor'));
    const isOwner = members.some((member) => {
        const memberId = member?.userId || member?.id || member?.uid;
        const role = String(member?.role || '').toUpperCase();
        return memberId && selfId && String(memberId) === String(selfId) && role === 'OWNER';
    });
    const canManageManga = isOwner || isModerator || isEditor;

    useEffect(() => {
        document.title = 'animix - Манга студии';
    }, []);

    const loadTeam = async () => {
        if (!hasSession || !isAllowed) {
            return;
        }
        setTeamLoading(true);
        setTeamError(null);
        try {
            const response = await MangaApiService.getMyTeam();
            const payload = unwrapResponse(response);
            setTeam(payload || null);
        } catch (err) {
            if (err?.response?.status === 404) {
                setTeam(null);
            } else {
                setTeamError(getMangaErrorMessage(err, 'Не удалось загрузить команду.'));
            }
        } finally {
            setTeamLoading(false);
        }
    };

    const loadTeamMangas = async (id) => {
        if (!id) {
            setMangaList([]);
            return;
        }
        setMangaLoading(true);
        setMangaListError(null);
        try {
            setMangaList(await fetchAllTeamMangas(id, { includeDeleted: canManageManga }));
        } catch (err) {
            setMangaListError(getMangaErrorMessage(err, 'Не удалось загрузить список манги.'));
        } finally {
            setMangaLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed || !hasSession) {
            return;
        }
        loadTeam();
    }, [isAllowed, hasSession]);

    useEffect(() => {
        if (!hasSession) {
            setSelfProfile(null);
            return;
        }
        let isActive = true;
        const loadSelf = async () => {
            try {
                const response = await ApiService.getSelf();
                if (isActive) {
                    setSelfProfile(response?.data || response);
                }
            } catch (err) {
                if (isActive) {
                    setSelfProfile(null);
                }
            }
        };
        loadSelf();
        return () => {
            isActive = false;
        };
    }, [hasSession]);

    useEffect(() => {
        if (!teamId || !hasSession) {
            setMembers([]);
            return;
        }
        let isActive = true;
        const loadMembers = async () => {
            setMembersLoading(true);
            setMembersError(null);
            try {
                const response = await MangaApiService.getTeamMembers(teamId);
                const list = unwrapList(response);
                if (isActive) {
                    setMembers(list);
                }
            } catch (err) {
                if (isActive) {
                    setMembersError(getMangaErrorMessage(err, 'Не удалось загрузить участников.'));
                    setMembers([]);
                }
            } finally {
                if (isActive) {
                    setMembersLoading(false);
                }
            }
        };
        loadMembers();
        return () => {
            isActive = false;
        };
    }, [teamId, hasSession]);

    useEffect(() => {
        if (!teamId) {
            setMangaList([]);
            setSelectedMangaId('');
            return;
        }
        loadTeamMangas(teamId);
    }, [teamId, canManageManga]);

    useEffect(() => {
        if (!mangaList.length) {
            return;
        }
        let cancelled = false;
        const loadCovers = async () => {
            const updates = {};
            for (const manga of mangaList) {
                const mangaId = getMangaId(manga);
                if (!mangaId || mangaCoverUrls[mangaId]) {
                    continue;
                }
                const direct = MangaApiService.resolveCoverUrl(manga);
                if (direct) {
                    updates[mangaId] = direct;
                    continue;
                }
                const resolved = await MangaApiService.resolveCoverUrlAsync(manga);
                if (resolved) {
                    updates[mangaId] = resolved;
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setMangaCoverUrls((prev) => ({ ...prev, ...updates }));
            }
        };
        loadCovers();
        return () => {
            cancelled = true;
        };
    }, [mangaList, mangaCoverUrls]);

    const selectedManga = useMemo(
        () => mangaList.find((item) => String(getMangaId(item)) === String(selectedMangaId)),
        [mangaList, selectedMangaId]
    );
    const selectedMangaStatus = selectedManga?.status || selectedManga?.state || selectedManga?.moderationStatus;
    const selectedMangaStatusLabel = translateStatus(selectedMangaStatus);
    const selectedMangaRejectionReason = getRejectionReason(selectedManga);
    const selectedMangaDeleted = isDeletedStatus(selectedMangaStatus)
        || Boolean(selectedManga?.deletedAt || selectedManga?.deleted_at || selectedManga?.isDeleted || selectedManga?.deleted);
    const selectedMangaCover = selectedMangaId
        ? (mangaCoverUrls[selectedMangaId] || MangaApiService.resolveCoverUrl(selectedManga))
        : null;

    useEffect(() => {
        if (!selectedManga && mangaList.length > 0) {
            const firstId = getMangaId(mangaList[0]);
            if (firstId !== undefined && firstId !== null) {
                setSelectedMangaId(String(firstId));
            }
            return;
        }
        if (!selectedManga) {
            setEditMangaForm(emptyMangaForm);
            setTransferTeamId('');
            return;
        }
        setTransferTeamId('');
        setEditMangaForm({
            title: selectedManga.title || selectedManga.name || '',
            description: selectedManga.description || selectedManga.summary || '',
            genres: Array.isArray(selectedManga.genres)
                ? selectedManga.genres.join(', ')
                : selectedManga.genres || selectedManga.tags || '',
            author: selectedManga.author || selectedManga.writer || selectedManga.artist || '',
            publisher: selectedManga.publisher || selectedManga.publisherName || selectedManga.publisher_name || '',
            animeId: selectedManga.animeId ?? selectedManga.anime_id ?? '',
            isNsfw: Boolean(selectedManga.isNsfw ?? selectedManga.is_nsfw ?? selectedManga.nsfw),
            isCompleted: Boolean(selectedManga.completed ?? selectedManga.isCompleted ?? selectedManga.is_completed),
            coverFileId: selectedManga.coverFileId || selectedManga.cover_file_id || selectedManga.cover?.fileId || '',
            coverStorageBucket: selectedManga.coverStorageBucket
                || selectedManga.cover_storage_bucket
                || selectedManga.cover?.bucket
                || '',
        });
    }, [selectedManga, mangaList]);

    const handleCoverUpload = async (event, mode = 'new') => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > COVER_MAX_SIZE_BYTES) {
            setMangaFormError('Обложка слишком большая. Максимум 3 МБ.');
            if (mode === 'new') {
                setCoverMessage(null);
            } else {
                setEditCoverMessage(null);
            }
            event.target.value = '';
            return;
        }
        if (mode === 'new') {
            setCoverMessage(null);
            setNewCoverUploading(true);
        } else {
            setEditCoverMessage(null);
            setEditCoverUploading(true);
        }
        try {
            const response = await MangaApiService.uploadFile(file);
            const payload = response?.data || response;
            const data = payload?.data || payload;
            const fileId = data?.id || data?.fileId || data?.file_id || payload?.id || payload?.fileId;
            const bucket = data?.bucket || data?.bucketName || data?.bucket_name;
            if (!fileId || !bucket) {
                throw new Error('Не удалось получить данные файла.');
            }
            const updateForm = mode === 'new' ? setNewMangaForm : setEditMangaForm;
            updateForm((prev) => ({
                ...prev,
                coverFileId: fileId,
                coverStorageBucket: bucket,
            }));
            const message = 'Обложка загружена.';
            if (mode === 'new') {
                setCoverMessage(message);
            } else {
                setEditCoverMessage(message);
            }
        } catch (err) {
            setMangaFormError(getImageUploadErrorMessage(err, 'Не удалось загрузить обложку.'));
        } finally {
            if (mode === 'new') {
                setNewCoverUploading(false);
            } else {
                setEditCoverUploading(false);
            }
            event.target.value = '';
        }
    };

    const TITLE_LIMIT = 128;
    const DESCRIPTION_LIMIT = 4000;
    const AUTHOR_LIMIT = 255;
    const PUBLISHER_LIMIT = 255;
    const GENRE_COUNT_LIMIT = 30;
    const GENRE_ITEM_LIMIT = 64;

    const normalizeAnimeId = (value) => {
        const raw = String(value ?? '').trim();
        if (!raw) {
            return { value: null, error: null };
        }
        const parsed = Number(raw);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return { value: null, error: 'Укажите корректный ID аниме.' };
        }
        return { value: Math.trunc(parsed), error: null };
    };

    const parseGenres = (value) => {
        if (Array.isArray(value)) {
            return value
                .map((item) => String(item ?? '').trim())
                .filter(Boolean);
        }
        return String(value ?? '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
    };

    const validateTitle = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed.length > TITLE_LIMIT) {
            return `Название не может быть длиннее ${TITLE_LIMIT} символов.`;
        }
        return null;
    };

    const validateDescription = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed.length > DESCRIPTION_LIMIT) {
            return `Описание не может быть длиннее ${DESCRIPTION_LIMIT} символов.`;
        }
        return null;
    };

    const validateAuthor = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed.length > AUTHOR_LIMIT) {
            return `Автор не может быть длиннее ${AUTHOR_LIMIT} символов.`;
        }
        return null;
    };

    const validatePublisher = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed.length > PUBLISHER_LIMIT) {
            return `Издатель не может быть длиннее ${PUBLISHER_LIMIT} символов.`;
        }
        return null;
    };

    const validateGenres = (value) => {
        const list = parseGenres(value);
        if (list.length > GENRE_COUNT_LIMIT) {
            return `Жанров может быть не больше ${GENRE_COUNT_LIMIT}.`;
        }
        const tooLong = list.find((genre) => genre.length > GENRE_ITEM_LIMIT);
        if (tooLong) {
            return `Каждый жанр не длиннее ${GENRE_ITEM_LIMIT} символов.`;
        }
        return null;
    };

    const buildMangaPayload = (form, includeTeam = false) => {
        const payload = {};
        const title = form.title.trim();
        const description = form.description.trim();
        const genres = parseGenres(form.genres);
        const author = form.author.trim();
        const publisher = form.publisher.trim();
        if (title) {
            payload.title = title;
            payload.name = title;
        }
        if (description) {
            payload.description = description;
            payload.summary = description;
        }
        payload.genres = genres;
        payload.author = author;
        if (author) {
            payload.writer = author;
            payload.artist = author;
        }
        if (publisher) {
            payload.publisher = publisher;
        }
        if (form.coverFileId) {
            payload.coverFileId = form.coverFileId;
            payload.cover_file_id = form.coverFileId;
        }
        if (form.coverStorageBucket) {
            payload.coverStorageBucket = form.coverStorageBucket;
            payload.cover_storage_bucket = form.coverStorageBucket;
        }
        payload.nsfw = Boolean(form.isNsfw);
        payload.completed = Boolean(form.isCompleted);
        const animeId = normalizeAnimeId(form.animeId).value;
        if (animeId !== null) {
            payload.animeId = animeId;
        }
        if (includeTeam && teamId) {
            payload.teamId = teamId;
        }
        return payload;
    };

    const handleCreateManga = async () => {
        setMangaMessage(null);
        setMangaFormError(null);
        if (!hasSession) {
            setMangaFormError('Войдите, чтобы создать мангу.');
            return;
        }
        if (!teamId) {
            setMangaFormError('Сначала создайте команду.');
            return;
        }
        if (!rightsAccepted) {
            setMangaFormError('Подтвердите права на публикацию.');
            return;
        }
        if (!newMangaForm.title.trim()) {
            setMangaFormError('Укажите название манги.');
            return;
        }
        const titleError = validateTitle(newMangaForm.title);
        if (titleError) {
            setMangaFormError(titleError);
            return;
        }
        const descriptionError = validateDescription(newMangaForm.description);
        if (descriptionError) {
            setMangaFormError(descriptionError);
            return;
        }
        const authorError = validateAuthor(newMangaForm.author);
        if (authorError) {
            setMangaFormError(authorError);
            return;
        }
        const publisherError = validatePublisher(newMangaForm.publisher);
        if (publisherError) {
            setMangaFormError(publisherError);
            return;
        }
        const genresError = validateGenres(newMangaForm.genres);
        if (genresError) {
            setMangaFormError(genresError);
            return;
        }
        const animeIdCheck = normalizeAnimeId(newMangaForm.animeId);
        if (animeIdCheck.error) {
            setMangaFormError(animeIdCheck.error);
            return;
        }
        try {
            await MangaApiService.createManga(buildMangaPayload(newMangaForm, true));
            setMangaMessage('Манга создана и отправлена на модерацию.');
            setNewMangaForm(emptyMangaForm);
            setCoverMessage(null);
            setRightsAccepted(false);
            await loadTeamMangas(teamId);
        } catch (err) {
            if (err?.response?.status === 409) {
                setMangaFormError('Название уже занято: измените его, чтобы получить другой slug.');
                return;
            }
            setMangaFormError(getMangaErrorMessage(err, 'Не удалось создать мангу.'));
        }
    };

    const handleUpdateManga = async () => {
        setMangaMessage(null);
        setMangaFormError(null);
        if (!hasSession) {
            setMangaFormError('Войдите, чтобы обновить мангу.');
            return;
        }
        const id = selectedMangaId || (selectedManga ? getMangaId(selectedManga) : null);
        if (!id) {
            setMangaFormError('Выберите мангу для редактирования.');
            return;
        }
        const titleError = validateTitle(editMangaForm.title);
        if (titleError) {
            setMangaFormError(titleError);
            return;
        }
        const descriptionError = validateDescription(editMangaForm.description);
        if (descriptionError) {
            setMangaFormError(descriptionError);
            return;
        }
        const authorError = validateAuthor(editMangaForm.author);
        if (authorError) {
            setMangaFormError(authorError);
            return;
        }
        const publisherError = validatePublisher(editMangaForm.publisher);
        if (publisherError) {
            setMangaFormError(publisherError);
            return;
        }
        const genresError = validateGenres(editMangaForm.genres);
        if (genresError) {
            setMangaFormError(genresError);
            return;
        }
        const animeIdCheck = normalizeAnimeId(editMangaForm.animeId);
        if (animeIdCheck.error) {
            setMangaFormError(animeIdCheck.error);
            return;
        }
        try {
            await MangaApiService.updateManga(id, buildMangaPayload(editMangaForm, false));
            setMangaMessage('Манга обновлена.');
            await loadTeamMangas(teamId);
        } catch (err) {
            setMangaFormError(getMangaErrorMessage(err, 'Не удалось обновить мангу.'));
        }
    };

    const handleDeleteManga = async () => {
        if (!selectedMangaId) {
            setMangaFormError('Выберите мангу для удаления.');
            return;
        }
        if (!canManageManga) {
            setMangaFormError('Удаление доступно только владельцу команды или модератору.');
            return;
        }
        const confirmDelete = window.confirm('Удалить выбранную мангу? В течение 7 дней доступно восстановление.');
        if (!confirmDelete) {
            return;
        }
        setMangaMessage(null);
        setMangaFormError(null);
        try {
            await MangaApiService.deleteManga(selectedMangaId);
            setMangaMessage('Манга удалена. В течение 7 дней можно восстановить.');
            await loadTeamMangas(teamId);
            setSelectedMangaId('');
        } catch (err) {
            setMangaFormError(getMangaErrorMessage(err, 'Не удалось удалить мангу.'));
        }
    };

    const handleRestoreManga = async () => {
        const id = selectedMangaId || (selectedManga ? getMangaId(selectedManga) : null);
        if (!id) {
            setMangaFormError('Выберите мангу для восстановления.');
            return;
        }
        if (!canManageManga) {
            setMangaFormError('Восстановление доступно только владельцу команды или модератору.');
            return;
        }
        setMangaMessage(null);
        setMangaFormError(null);
        try {
            await MangaApiService.restoreManga(id);
            setMangaMessage('Манга восстановлена.');
            await loadTeamMangas(teamId);
        } catch (err) {
            setMangaFormError(getMangaErrorMessage(err, 'Не удалось восстановить мангу.'));
        }
    };

    const handleTransferManga = async () => {
        const id = selectedMangaId || (selectedManga ? getMangaId(selectedManga) : null);
        if (!id) {
            setMangaFormError('Выберите мангу для переноса.');
            return;
        }
        const targetId = transferTeamId.trim();
        if (!targetId) {
            setMangaFormError('Укажите ID команды для переноса.');
            return;
        }
        if (!canManageManga) {
            setMangaFormError('Перенос доступен только владельцу команды или модератору.');
            return;
        }
        const confirmTransfer = window.confirm('Перенести тайтл в другую команду?');
        if (!confirmTransfer) {
            return;
        }
        setTransferBusy(true);
        setMangaMessage(null);
        setMangaFormError(null);
        try {
            await MangaApiService.transferManga(id, Number.isNaN(Number(targetId)) ? targetId : Number(targetId));
            setMangaMessage('Тайтл перенесён в другую команду.');
            setTransferTeamId('');
            await loadTeamMangas(teamId);
        } catch (err) {
            setMangaFormError(getMangaErrorMessage(err, 'Не удалось перенести тайтл.'));
        } finally {
            setTransferBusy(false);
        }
    };

    const openLightbox = (src, title) => {
        if (!src) {
            return;
        }
        setLightboxImage(src);
        setLightboxTitle(title || 'Обложка манги');
    };

    const closeLightbox = () => {
        setLightboxImage(null);
        setLightboxTitle('');
    };

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    const normalizedTitleQuery = titleQuery.trim().toLowerCase();
    const filteredMangaList = normalizedTitleQuery
        ? mangaList.filter((manga) => getMangaTitle(manga).toLowerCase().includes(normalizedTitleQuery))
        : mangaList;

    const showEditor = Boolean(selectedManga) && !composeNew;
    const formDisabled = !hasSession || !teamId;

    const renderFields = (form, setForm, disabled, suffix) => (
        <>
            <label className="manga-field">
                <span>Название</span>
                <input
                    type="text"
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    maxLength={TITLE_LIMIT}
                    disabled={disabled}
                />
            </label>
            <label className="manga-field">
                <span>Описание</span>
                <textarea
                    value={form.description}
                    onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                    maxLength={DESCRIPTION_LIMIT}
                    disabled={disabled}
                />
            </label>
            <div className="manga-field">
                <span>Жанры</span>
                <GenrePicker
                    value={form.genres}
                    options={genreOptions}
                    disabled={disabled}
                    onChange={(genres) => setForm((prev) => ({ ...prev, genres }))}
                />
                <input
                    type="text"
                    placeholder="Или впишите через запятую"
                    value={form.genres}
                    onChange={(event) => setForm((prev) => ({ ...prev, genres: event.target.value }))}
                    disabled={disabled}
                />
            </div>
            <div className="manga-field-row studio-fields-3">
                <label className="manga-field">
                    <span>Автор</span>
                    <input
                        type="text"
                        value={form.author}
                        onChange={(event) => setForm((prev) => ({ ...prev, author: event.target.value }))}
                        maxLength={AUTHOR_LIMIT}
                        disabled={disabled}
                    />
                </label>
                <label className="manga-field">
                    <span>Издатель</span>
                    <input
                        type="text"
                        value={form.publisher}
                        onChange={(event) => setForm((prev) => ({ ...prev, publisher: event.target.value }))}
                        maxLength={PUBLISHER_LIMIT}
                        disabled={disabled}
                    />
                </label>
                <label className="manga-field">
                    <span>ID аниме на animix</span>
                    <input
                        type="number"
                        placeholder="Необязательно"
                        value={form.animeId}
                        onChange={(event) => setForm((prev) => ({ ...prev, animeId: event.target.value }))}
                        disabled={disabled}
                    />
                </label>
            </div>
            <div className="studio-toggles">
                <div className="manga-toggle">
                    <input
                        type="checkbox"
                        id={`manga-nsfw-${suffix}`}
                        checked={form.isNsfw}
                        onChange={(event) => setForm((prev) => ({ ...prev, isNsfw: event.target.checked }))}
                        disabled={disabled}
                    />
                    <label className="label-switch" htmlFor={`manga-nsfw-${suffix}`} />
                    <span className="label-switch-text">NSFW</span>
                </div>
                <div className="manga-toggle">
                    <input
                        type="checkbox"
                        id={`manga-completed-${suffix}`}
                        checked={form.isCompleted}
                        onChange={(event) => setForm((prev) => ({ ...prev, isCompleted: event.target.checked }))}
                        disabled={disabled}
                    />
                    <label className="label-switch" htmlFor={`manga-completed-${suffix}`} />
                    <span className="label-switch-text">Перевод завершён</span>
                </div>
            </div>
        </>
    );

    return (
        <PageShell className="manga-studio-page">
            <PageHeader title="Манга" eyebrow="Студия манги" />
            <MangaStudioNav />

            {!hasSession ? <div className="manga-inline-warning">Войдите, чтобы управлять мангой.</div> : null}
            {teamError ? <div className="manga-inline-warning">{teamError}</div> : null}
            {membersError ? <div className="manga-inline-warning">{membersError}</div> : null}
            {!teamId && !teamLoading && hasSession ? (
                <div className="manga-inline-warning">
                    Сначала создайте команду в разделе <Link className="manga-rules-link" to="/manga/studio/team">«Команда»</Link>.
                </div>
            ) : null}

            <div className="studio-layout">
                {/* Left: the team's titles. Picking one opens it in the editor on the right. */}
                <aside className="studio-layout__side">
                    <div className="manga-panel">
                        <div className="studio-card__head">
                            <h2>Тайтлы{mangaList.length ? <small>{mangaList.length}</small> : null}</h2>
                            <button
                                type="button"
                                className={`ui-btn ui-btn--sm${composeNew || !selectedManga ? '' : ' ui-btn--secondary'}`}
                                onClick={() => { setComposeNew(true); }}
                                disabled={!teamId}
                            >
                                Новая
                            </button>
                        </div>
                        {teamLoading || membersLoading || mangaLoading ? <div className="loader" /> : null}
                        {mangaListError ? <div className="manga-inline-warning">{mangaListError}</div> : null}
                        {mangaList.length > 6 ? (
                            <label className="manga-field">
                                <span className="manga-visually-hidden">Найти тайтл команды</span>
                                <input
                                    type="search"
                                    placeholder={`Поиск среди ${mangaList.length} тайтлов`}
                                    value={titleQuery}
                                    onChange={(event) => setTitleQuery(event.target.value)}
                                />
                            </label>
                        ) : null}
                        <div className="manga-studio-list manga-studio-list--scroll">
                            {filteredMangaList.length > 0 ? (
                                filteredMangaList.map((manga) => {
                                    const id = getMangaId(manga);
                                    const title = getMangaTitle(manga);
                                    const status = manga.status || manga.state || manga.moderationStatus;
                                    const coverUrl = id
                                        ? (mangaCoverUrls[id] || MangaApiService.resolveCoverUrl(manga))
                                        : MangaApiService.resolveCoverUrl(manga);
                                    const isSelected = showEditor && String(id) === String(selectedMangaId);
                                    return (
                                        <button
                                            key={id || title}
                                            className={`manga-studio-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => { setSelectedMangaId(String(id)); setComposeNew(false); }}
                                        >
                                            <div className="manga-studio-item-content">
                                                <div className="manga-cover-thumb static">
                                                    <img src={coverUrl || placeholderCover} alt="" loading="lazy" />
                                                </div>
                                                <div className="manga-studio-item-details">
                                                    <div className="manga-studio-item-title">{title}</div>
                                                    {status ? <div className="manga-studio-item-meta">{translateStatus(status)}</div> : null}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="manga-empty">
                                    {mangaList.length > 0 ? 'Ничего не найдено.' : 'Тайтлов пока нет.'}
                                </div>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Right: one form — a new title, or the selected one. */}
                <div className="studio-layout__main">
                    {!showEditor ? (
                        <div className="manga-panel">
                            <div className="studio-card__head">
                                <h2>Новая манга</h2>
                                {selectedManga ? (
                                    <button type="button" className="button-sec" onClick={() => setComposeNew(false)}>К выбранному тайтлу</button>
                                ) : null}
                            </div>
                            {mangaFormError ? <div className="manga-inline-warning">{mangaFormError}</div> : null}
                            {renderFields(newMangaForm, setNewMangaForm, formDisabled, 'new')}
                            <div className="manga-field">
                                <span>Обложка</span>
                                <div className="manga-studio-toolbar">
                                    <label className="upload-label">
                                        {newCoverUploading ? 'Загрузка обложки...' : 'Загрузить обложку'}
                                        <input type="file" accept="image/*" onChange={(event) => handleCoverUpload(event, 'new')} disabled={formDisabled || newCoverUploading} />
                                    </label>
                                    <span className="manga-help">JPG/PNG до 3 МБ.</span>
                                </div>
                                {coverMessage ? <div className="manga-inline-note">{coverMessage}</div> : null}
                            </div>
                            <div className="manga-toggle">
                                <input
                                    type="checkbox"
                                    id="manga-rights"
                                    checked={rightsAccepted}
                                    onChange={(event) => setRightsAccepted(event.target.checked)}
                                    disabled={formDisabled}
                                />
                                <label className="label-switch" htmlFor="manga-rights" />
                                <span className="label-switch-text">Я подтверждаю, что владею всеми правами для публикации</span>
                            </div>
                            <div className="studio-card__footer">
                                <button onClick={handleCreateManga} disabled={formDisabled || !rightsAccepted}>Создать мангу</button>
                                <span className="manga-help">
                                    Адрес страницы формируется из названия. Тайтл уйдёт на модерацию — <Link className="manga-rules-link" to="/manga/rules">правила</Link>.
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="manga-panel">
                            <div className="manga-studio-summary studio-summary--manga">
                                <button
                                    type="button"
                                    className={`manga-cover-thumb large${selectedMangaCover ? '' : ' is-empty'}`}
                                    onClick={() => openLightbox(selectedMangaCover, getMangaTitle(selectedManga))}
                                    disabled={!selectedMangaCover}
                                >
                                    <img src={selectedMangaCover || placeholderCover} alt={getMangaTitle(selectedManga)} loading="lazy" />
                                </button>
                                <div className="studio-summary__body">
                                    <h2>{getMangaTitle(selectedManga)}</h2>
                                    {selectedMangaStatus ? <span className="studio-status">{selectedMangaStatusLabel}</span> : null}
                                    <div className="manga-help">
                                        {normalizeStatus(selectedMangaStatus) === 'published'
                                            ? 'Тайтл опубликован: изменения появятся сразу, без повторной модерации.'
                                            : 'Изменения отправят тайтл на модерацию.'}
                                    </div>
                                    {isRejectedStatus(selectedMangaStatus) && selectedMangaRejectionReason ? (
                                        <div className="manga-inline-warning">Причина отклонения: {selectedMangaRejectionReason}</div>
                                    ) : null}
                                    <div className="manga-studio-toolbar">
                                        <label className="upload-label">
                                            {editCoverUploading ? 'Загрузка обложки...' : 'Обновить обложку'}
                                            <input type="file" accept="image/*" onChange={(event) => handleCoverUpload(event, 'edit')} disabled={!hasSession || editCoverUploading} />
                                        </label>
                                        <Link className="button-sec" to={`/manga/studio/chapters?manga=${selectedMangaId}`}>Главы</Link>
                                    </div>
                                    {editCoverMessage ? <div className="manga-inline-note">{editCoverMessage}</div> : null}
                                </div>
                            </div>
                            {mangaFormError ? <div className="manga-inline-warning">{mangaFormError}</div> : null}
                            {renderFields(editMangaForm, setEditMangaForm, !hasSession, 'edit')}
                            <div className="studio-card__footer">
                                <button onClick={handleUpdateManga} disabled={!hasSession}>Сохранить изменения</button>
                                {mangaMessage ? <span className="manga-inline-note">{mangaMessage}</span> : null}
                            </div>
                            {selectedMangaDeleted ? (
                                <div className="manga-inline-warning">
                                    Тайтл удалён. В течение 7 дней его можно восстановить.{' '}
                                    <button type="button" className="manga-rules-link studio-link-button" onClick={handleRestoreManga} disabled={!hasSession || !canManageManga}>Восстановить</button>
                                </div>
                            ) : null}
                            {canManageManga ? (
                                <details className="studio-details studio-details--inline">
                                    <summary>Перенос и удаление</summary>
                                    <div className="manga-field-row">
                                        <input
                                            type="text"
                                            placeholder="ID команды для переноса"
                                            value={transferTeamId}
                                            onChange={(event) => setTransferTeamId(event.target.value)}
                                            disabled={!hasSession || transferBusy}
                                        />
                                        <button type="button" className="button-sec" onClick={handleTransferManga} disabled={!hasSession || transferBusy}>
                                            {transferBusy ? 'Перенос...' : 'Перенести тайтл'}
                                        </button>
                                    </div>
                                    <button className="manga-danger" onClick={handleDeleteManga} disabled={!hasSession}>Удалить мангу</button>
                                </details>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
            <MangaImageLightbox src={lightboxImage} alt={lightboxTitle} onClose={closeLightbox} />
        </PageShell>
    );
};

export default MangaStudioMangaPage;
